import { useCallback } from "react";

import { useModals } from "hooks";
import { uniqBy } from "lodash";
import { useAccountSelector } from "store";
import { CurrencyType } from "store/types";

import {
  Errors,
  getDefaultMeasureData,
  Group,
  IGroupsFormContext,
  MeasureData,
  MeasureDataErrors,
  MeasureDataRound,
  UpdateMeasurePayloadPayload
} from "../GroupsFormContext";

interface AcceptParams {
  getMaxMeasureDataKey: () => number;
  setMeasureData: React.Dispatch<React.SetStateAction<MeasureData[]>>;
  setRounds: React.Dispatch<React.SetStateAction<Record<number, MeasureDataRound[]>>>;
  setMeasureDataErrors: React.Dispatch<React.SetStateAction<Record<number, MeasureDataErrors> | undefined>>;

  measureData: MeasureData[];
  groups: Group[];
  rounds: Record<number, MeasureDataRound[]>;

  batch_id?: number;
  defaultCurrency: CurrencyType;
}

type UseMeasureData = (
  params: AcceptParams
) => Pick<
  IGroupsFormContext,
  "createMeasureData" | "deleteMeasureData" | "updateMeasureData" | "updateManyMeasureData" | "updateGroupMeasureData"
>;

export const useMeasureData: UseMeasureData = ({
  getMaxMeasureDataKey,
  setMeasureData,
  setRounds,
  setMeasureDataErrors,

  measureData,
  groups,
  rounds,

  batch_id,
  defaultCurrency
}) => {
  const { confirmModal } = useModals();
  const { allow_split_by_group } = useAccountSelector() || {};

  const createMeasureData = useCallback<IGroupsFormContext["createMeasureData"]>(
    async ({
      round_id,
      group_id,
      investor_id,
      investor_name,
      investor_group,
      value,
      date,
      currency,
      accounting_date,
      effective_range_from,
      effective_range_to
    }) => {
      // Flow to create measure_data for multiple groups with the same deal id
      // Find group to define which deal's rounds were used.
      const selectedGroup = groups.find((group) => group.id === group_id);
      if (!selectedGroup) {
        return console.log(Errors.COULD_NOT_FIND_GROUP);
      }
      // Effective deal id is one that takes rounds.
      // If the group has sub_deal_id it uses rounds of sub deal otherwise it uses current deal.
      const effectiveDealId = selectedGroup.sub_deal_id || selectedGroup.deal_id;

      // Check if the selected round includes this investor

      const roundsByDeal = rounds[effectiveDealId];
      const isRoundIncludesInvestor = roundsByDeal
        ?.find((round) => round.id === round_id)
        ?.investors.some((inv) => inv.id === investor_id);
      const allInvestorsFromDeal = uniqBy(roundsByDeal.map((item) => item.investors).flat(), "id");
      const isInvestorFromDeal = allInvestorsFromDeal.some((inv) => inv.id === investor_id);

      // Prevent adding same investor twice to the round
      if (isRoundIncludesInvestor) throw Errors.SAME_INVESTOR_PER_ROUND;

      // Ask for confirmation if the investor is not from the deal
      // If there are no investors in the deal, skip confirmation
      if (!isInvestorFromDeal && allInvestorsFromDeal.length && !allow_split_by_group) {
        await new Promise((resolve, reject) =>
          confirmModal({
            action: () => {
              resolve(true);
            },
            msg: "You are adding a measure for an investor who is not currently associated with this deal. Are you sure you want to proceed?",
            title: "New Investor Detected",
            onCancel: () => {
              reject(Errors.CANCEL_CONFIRMATION);
            }
          })
        );
      }

      // All groups ids with the same deal id
      const groupsIds = groups
        .filter((group) => (group.sub_deal_id || group.deal_id) === effectiveDealId)
        .map((item) => item.id);

      // Add measure_data - compute maxKey from prevState to handle concurrent calls
      setMeasureData((prevState) => {
        const maxKey = prevState.reduce((acc, item) => (item.key > acc ? item.key : acc), 0);
        const result: MeasureData[] = [];
        // This is will be the new key for newly created measure_data
        let counter = maxKey + 1;
        groupsIds.forEach((groupId) => {
          const basicMeasureData = getDefaultMeasureData({
            key: counter,
            group_id: groupId,
            round_id,
            investor_id,
            investor_name,
            investor_group,
            isDeletable: true,
            ...(date && { date }),
            ...(currency && { currency }),
            ...(accounting_date && { accounting_date }),
            ...(effective_range_from && { effective_range_from }),
            ...(effective_range_to && { effective_range_to }),
            ...(group_id === groupId && { value }),
            ...(batch_id && { batch_id })
          });
          // On this step we have to find at least 1 measure_data for the same group and round.
          // We need it to define the currency and dates of same section(group and round)
          const measureSameRound = prevState.find((item) => item.group_id === groupId && item.round_id === round_id);
          if (measureSameRound) {
            basicMeasureData.currency = measureSameRound.currency;
            basicMeasureData.accounting_date = measureSameRound.accounting_date;
            basicMeasureData.date = measureSameRound.date;
          }
          if (!basicMeasureData.currency) {
            basicMeasureData.currency = defaultCurrency;
          }
          result.push(basicMeasureData);
          counter++;
        });

        return [...prevState, ...result];
      });

      // Add investor to the round
      setRounds((prevState) => {
        if (!prevState) return prevState;
        return {
          ...prevState,
          [effectiveDealId]: prevState[effectiveDealId].map((round) => {
            if (round.id !== round_id) return round;
            return {
              ...round,
              investors: [
                ...round.investors,
                { id: investor_id!, name: investor_name, enable_split: false, investor_group: investor_group ?? null }
              ]
            };
          })
        };
      });
    },
    [measureData, rounds, groups, getMaxMeasureDataKey, setMeasureData, batch_id, defaultCurrency]
  );

  const deleteMeasureData = useCallback<IGroupsFormContext["deleteMeasureData"]>(
    (key) => {
      const { group_id, round_id, investor_id } = measureData.find((item) => item.key === key) || {};

      if (!investor_id) {
        return setMeasureData((prevState) => prevState.filter((item) => item.key !== key));
      }

      if (!group_id || !round_id) return;

      const group = groups.find((group) => group.id === group_id);

      if (!group) return;

      const effectiveDealId = group.sub_deal_id || group.deal_id;
      confirmModal({
        action: () => {
          // Remove all measure_data by this investor in the round.
          setMeasureData((prevState) =>
            prevState.filter((item) => {
              if (item.investor_id === investor_id && item.round_id === round_id) return false;
              return item;
            })
          );
          // Remove investor from the round.
          setRounds((prevState) => {
            if (!prevState) return prevState;
            return {
              ...prevState,
              [effectiveDealId]: prevState[effectiveDealId].map((round) => {
                if (round.id !== round_id) return round;

                return { ...round, investors: round.investors.filter((investor) => investor.id !== investor_id) };
              })
            };
          });
        },
        msg: "Delete monitoring data item?",
        title: "Monitoring data"
      });
    },
    [setMeasureData, confirmModal, measureData, groups]
  );

  const updateMeasureData = useCallback<IGroupsFormContext["updateMeasureData"]>(
    ({ key, ...rest }) => {
      setMeasureData((prevState) => prevState.map((item) => (item.key === key ? { ...item, ...rest } : item)));
      clearMeasureDataError(key, rest);
    },
    [setMeasureData, measureData, groups, getMaxMeasureDataKey, defaultCurrency]
  );

  const updateGroupMeasureData = useCallback<IGroupsFormContext["updateGroupMeasureData"]>(
    (round_id, investors, group_id) => {
      setMeasureData((prevState) =>
        prevState.map((item) => {
          if (item.round_id !== round_id || item.group_id !== group_id) return item;
          const investor = investors.find((inv) => inv.id === item.investor_id);
          if (!investor) return item;
          return { ...item, value: investor.value };
        })
      );
    },
    [setMeasureData]
  );

  const updateManyMeasureData = useCallback<IGroupsFormContext["updateManyMeasureData"]>(
    (keys, payload) => {
      keys.forEach((key) => updateMeasureData({ key, ...payload }));
    },
    [updateMeasureData]
  );

  const clearMeasureDataError = useCallback(
    (key: number, data: UpdateMeasurePayloadPayload) => {
      setMeasureDataErrors((prevState) => {
        if (!prevState) return;
        if (!prevState[key]) return prevState;

        const errorsCopy = { ...prevState };

        delete errorsCopy[key]["entity"];
        Object.keys(data).forEach((fields) => {
          delete errorsCopy[key][fields as keyof MeasureDataErrors];
        });

        if (!Object.keys(errorsCopy[key]).length) {
          delete errorsCopy[key];
        }

        if (!Object.keys(errorsCopy).length) return;

        return errorsCopy;
      });
    },
    [setMeasureDataErrors]
  );

  return {
    createMeasureData,
    deleteMeasureData,
    updateMeasureData,
    updateManyMeasureData,
    updateGroupMeasureData
  };
};

// // Old flow to create measure_data
// const maxKey = getMaxMeasureDataKey();
// const newMeasureData = getDefaultMeasureData({
//   key: maxKey + 1,
//   group_id,
//   round_id,
//   // round_name,
//   isDeletable: true,
//   ...(batch_id && { batch_id })
// });
// const measureSameRound = measureData.find((item) => item.group_id === group_id && item.round_id === round_id);
// if (measureSameRound) {
//   newMeasureData.currency = measureSameRound.currency;
//   newMeasureData.accounting_date = measureSameRound.accounting_date;
//   newMeasureData.date = measureSameRound.date;
// }

// if (!newMeasureData.currency) {
//   newMeasureData.currency = defaultCurrency;
// }

// setMeasureData((prevState) => [...prevState, newMeasureData]);
